import { useState, forwardRef } from 'react';
import Typography from '@mui/material/Typography';
import { Button as MuiButton } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';

import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Tune as TuneIcon,
  Menu as MenuIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ViewWeek as ViewWeekIcon
} from '@mui/icons-material';

import classes from './classes.module.scss';
import ColumnsModal from './ColumnsModal';

const Button = props => {
  const {
    // key,  // React complains when attempting to read the passed key prop
    className,
    variant = 'contained',
    color = 'secondary',
    icon,
    startIcon,
    endIcon,
    onClick,
    disabled,
    label,
    children
  } = props;

  let sx = {
    margin: '0.5rem',
    boxShadow: 'none',
    fontSize: '1rem',
    textTransform: 'none'
  }

  if (props.sx) {
    sx = { ...sx, ...props.sx };
  }

  return (
    <MuiButton
      // key={buttonId}
      className={className}
      variant={variant}
      color={color}
      sx={sx}
      startIcon={icon || startIcon}
      endIcon={endIcon}
      disabled={disabled}
      onClick={onClick}>
      {label}
      {children}
    </MuiButton>
  )
}

const Select = props => {
  const {
    label,
    startIcon,
    endIcon,
    disabled,
    isMobile,
    menuItems,
    onOpen,
    keepOpen
  } = props;

  const [state, setState] = useState({
    isOpen: false,
    anchorEl: null
  });

  const onClick = event => {
    setState({
      isOpen: true,
      anchorEl: event.target
    });

    if (onOpen) {
      onOpen();
    }
  }

  const closeMenu = () => {
    setState({
      isOpen: false,
      anchorEl: null
    });
  }

  let button;
  if (isMobile) {
    button = (
      <Button
        endIcon={endIcon}
        onClick={onClick}
        disabled={disabled}
      >
        {startIcon}
      </Button>
    )
  } else {
    button = (
      <Button
        startIcon={startIcon}
        endIcon={endIcon}
        onClick={onClick}
        disabled={disabled}
      >
        {label}
      </Button>
    )
  }

  return (
    <>
      {button}
      <Menu anchorEl={state.anchorEl} open={state.isOpen} onClose={closeMenu}>
	{menuItems.map(item => {
	  const onItemClick = () => {
	    item.onClick();

	    if (!keepOpen) {
	      closeMenu();
	    }
	  }

	  if (item.checkbox) {
	    return (
	      <MenuItem key={item.key} value={item.value}>
		<Checkbox checked={item.checked} onClick={onItemClick} />
		<ListItemText primary={item.label} />
	      </MenuItem>
	    )
	  } else {
	    return (
	      <MenuItem key={item.key} value={item.value} onClick={onItemClick}>
		<ListItemText primary={item.label} />
	      </MenuItem>
	    )
	  }
	})}
      </Menu>
    </>
  )
}

const Header = props => {
  const { header } = props;
  if (!header) {
    return null;
  }

  return (
    <Typography
      className={classes.header}
      variant="h1"
    >
      {header}
    </Typography>
  );
}

const AddButton = props => {
  const {
    dataGrid,
    addButtonLabel,
    isMobile
  } = props;

  const {
    quickEdit,
    showAdd,
    onAdd
  } = dataGrid;

  if (!showAdd) {
    return null;
  }

  // hide when quick edit is active for now.
  // ideally the button would remain and add a new row when clicked,
  // but currently with AgGrid's data source row model this is difficult.
  // early on we had this working by dynamically switching to a static
  // row model when quick edit mode is active.
  // we'll probably need to do that here, which might limit the editable
  // items to the current page
  if (quickEdit.active) {
    return null;
  }

  let onClick = quickEdit.active ? quickEdit.onAdd : onAdd;
  let icon = <AddIcon />;

  // show button as white (non-primary) if quick edit mode is active
  let color = quickEdit.active ? 'secondary' : 'primary';

  if (isMobile) {
    return (
      <Button color={color} onClick={onClick}>
        {icon}
      </Button>
    );
  } else {
    return (
      <Button color={color} startIcon={icon} onClick={onClick}>
        {addButtonLabel}
      </Button>
    );
  }
}

const QuickEditButton = props => {
  const {
    dataGrid,
    isMobile
  } = props;

  const {
    quickEdit,
    toggleQuickEdit,
    commitQuickEditChanges
  } = dataGrid;

  // only show button if quick edit mode is enabled
  if (!quickEdit.enabled) {
    return null;
  }

  // if quick edit mode is active then render as Save Changes button
  let label, color, onClick, icon;
  if (quickEdit.active) {
    label = isMobile ? 'Save' : 'Save Changes';
    color = 'primary';
    onClick = commitQuickEditChanges;
    icon = <CheckIcon />;
  } else {
    label = 'Quick Edit';
    color = 'secondary';
    onClick = toggleQuickEdit;
    icon = <EditIcon />;
  }

  // hide label if mobile, unless quick edit mode is active
  if (isMobile && !quickEdit.active) {
    return (
      <Button color={color} onClick={onClick}>
        {icon}
      </Button>
    );
  } else {
    return (
      <Button color={color} startIcon={icon} onClick={onClick}>
        {label}
      </Button>
    );
  }
}

const DiscardEditButton = props => {
  const {
    dataGrid,
    isMobile
  } = props;

  const {
    quickEdit,
    columns
  } = dataGrid;

  // only show button if quick edit mode is active
  if (!quickEdit.active) {
    return null;
  }

  let label = isMobile ? 'Cancel' : 'Discard Changes';
  let onClick = columns.toggleQuickEdit;
  let icon = <CloseIcon />;

  return (
    <Button startIcon={icon} onClick={onClick}>
      {label}
    </Button>
  );
}

const QuickEditDivider = props => {
  const { quickEdit } = props.dataGrid;
  if (quickEdit.active) {
    return <Divider orientation="vertical" flexItem />;
  }
  return null;
}

const ActionsSelect = props => {
  const {
    dataGrid,
    actions,
    isMobile
  } = props;

  const { actionsSelect } = dataGrid;

  // if mobile or actions select is hidden then hide select
  if (isMobile || actionsSelect.hidden) {
    return null;
  }

  let label = 'Actions';
  let startIcon = <MenuIcon />;
  let endIcon = <ArrowDropDownIcon />;
  let disabled = actionsSelect.disabled;

  let menuItems = actions.map(action => ({
    key: `action-${action.key}`,
    label: action.label,
    value: action.value || action.key,
    onClick: () => actionsSelect.menuOnClick(action)
  }));

  return (
    <Select {...{
      label,
      startIcon,
      endIcon,
      disabled,
      menuItems
    }} />
  )
}

const DropdownSelect = props => {
  const {
    dataGrid,
    dropDownLabel,
    isMobile
  } = props;

  const { dropDownSelect } = dataGrid;
  const [selectLabel, setSelectLabel] = useState(dropDownLabel);

  // if mobile or actions select is hidden then hide select
  if (isMobile || dropDownSelect.hidden) {
    return null;
  }

  let label = selectLabel;
  let startIcon = <MenuIcon />;
  let endIcon = <ArrowDropDownIcon />;
  let disabled = dropDownSelect.disabled;

  let menuItems = dropDownSelect.dropDownOptions.map(action => ({
    key: `option-${option.key}`,
    label: option.label,
    value: option.value || option.key,
    onClick: () => {
      dropDownSelect.optionOnClick(option);
      setSelectLabel(option.label);
    }
  }));

  return (
    <Select {...{
      label,
      startIcon,
      endIcon,
      disabled,
      menuItems
    }} />
  )
}

const FiltersButton = props => {
  const {
    dataGrid,
    isMobile
  } = props;

  const {
    filtersButton,
    agGrid,
    quickEdit
  } = dataGrid;

  const [isActive, setIsActive] = useState(agGrid.filtersActiveRef.current);

  if (quickEdit.active) {
    return null;
  }

  const onClick = () => {
    setIsActive(!isActive);
    filtersButton.onClick();
  }

  let label = 'Filters';
  let icon = <TuneIcon />;
  let disabled = filtersButton.disabled;
  let className = !disabled && isActive ? 'pressed' : null;

  if (isMobile) {
    return (
      <Button className={className} onClick={onClick} disabled={disabled}>
        {icon}
      </Button>
    )
  } else {
    return (
      <Button className={className} startIcon={icon} onClick={onClick} disabled={disabled}>
        {label}
      </Button>
    );
  }
}

const ColumnsButton = props => {
  const {
    isMobile,
    dataGrid,
    openColumnsModal,
    closeColumnsModal
  } = props;

  const {
    quickEdit
  } = dataGrid;

  const [isActive, setIsActive] = useState(false);

  // if mobile or select is hidden then hide select
  if (isMobile || quickEdit.active) {
    return null;
  }

  const onClick = () => {
    if (isActive) {
      setIsActive(false);
      closeColumnsModal();
    } else {
      setIsActive(true);
      openColumnsModal();
    }
  }

  let label = 'Columns';
  let startIcon = <ViewWeekIcon />;
  let className = isActive ? 'pressed' : null;

  return (
    <Button {...{
      className,
      startIcon,
      onClick
    }}>
      {label}
    </Button>
  );
}

const TopBar = props => {
  const {
    resource,
    dataGrid,
    header,
    isMobile,
    onAdd,
    addButtonLabel,
    actions,
    dropDownLabel,
    buttons
  } = props;

  const [columnsModalIsOpen, setColumnsModalIsOpen] = useState(false);
  const openColumnsModal = () => setColumnsModalIsOpen(true);
  const closeColumnsModal = () => setColumnsModalIsOpen(false);

  const {
    columns,
    quickEdit
  } = dataGrid;

  const addButton = (
    <AddButton {...{
      dataGrid,
      isMobile,
      onAdd,
      addButtonLabel
    }} />
  );

  const quickEditButton = (
    <QuickEditButton {...{
      dataGrid,
      isMobile
    }} />
  );

  const discardEditButton = (
    <DiscardEditButton {...{
      dataGrid,
      isMobile
    }} />
  );

  const divider = (
    <QuickEditDivider {...{
      dataGrid
    }} />
  );

  const actionsSelect = (
    <ActionsSelect {...{
      dataGrid,
      actions,
      isMobile
    }} />
  );

  const filtersButton = (
    <FiltersButton {...{
      dataGrid,
      isMobile
    }} />
  );

  const columnsButton = (
    <ColumnsButton {...{
      isMobile,
      dataGrid,
      openColumnsModal,
      closeColumnsModal
    }} />
  );

  const dropDownSelect = (
    <DropdownSelect {...{
      dataGrid,
      dropDownLabel,
      isMobile
    }} />
  );

  let customButtons = null;
  if (buttons && !quickEdit.active) {
    customButtons = buttons.map(btn => <Button { ...btn } />);
  }

  let actionsPanel;
  if (isMobile) {
    if (dataGrid.quickEdit.active) {
      actionsPanel = (
        <div className={classes.actions}>
          {addButton}
          {discardEditButton}
          {quickEditButton}
        </div>
      );
    } else {
      actionsPanel = (
        <div className={classes.actions}>
          {actionsSelect}
          {dropDownSelect}
	  {customButtons}
          {filtersButton}
          {columnsButton}
          {quickEditButton}
          {discardEditButton}
          {addButton}
        </div>
      );
    }
  } else {
    actionsPanel = (
      <div className={classes.actions}>
        {actionsSelect}
        {dropDownSelect}
	{customButtons}
        {filtersButton}
        {columnsButton}
        {divider}
        {quickEditButton}
        {discardEditButton}
        {divider}
        {addButton}
      </div>
    );
  }

  return (
    <div>
      <Header header={header} />
      <div className={classes.toolbar}>
        {actionsPanel}
      </div>
      <ColumnsModal
	isOpen={columnsModalIsOpen}
	onClose={closeColumnsModal}
	resource={resource}
	columns={columns}
      />
    </div>
  );
}

export default TopBar;
